import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../services/adminAPI';
import Navbar from '../components/Navbar';
import './AdminAnnouncements.css';

function AdminAnnouncements() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    message: '',
    targetTier: 'all',
    startDate: '',
    endDate: '',
    linkUrl: '',
    linkText: '',
  });

  useEffect(() => {
    if (user && !user.isAdmin) {
      navigate('/');
      return;
    }
    fetchAnnouncements();
  }, [user, navigate]);

  const fetchAnnouncements = async () => {
    try {
      const response = await adminAPI.getAllAnnouncements();
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Error fetching announcements:', error);
      alert('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Convert datetime-local to proper UTC ISO string
    const submitData = {
      ...formData,
      startDate: formData.startDate
        ? new Date(formData.startDate).toISOString()
        : null,
      endDate: formData.endDate
        ? new Date(formData.endDate).toISOString()
        : null,
      linkUrl: formData.linkUrl || null,
      linkText: formData.linkText || null,
    };

    try {
      if (editingId) {
        await adminAPI.updateAnnouncement(editingId, submitData);
        alert('Announcement updated successfully!');
      } else {
        await adminAPI.createAnnouncement(submitData);
        alert('Announcement created successfully!');
      }

      resetForm();
      fetchAnnouncements();
    } catch (error) {
      console.error('Error saving announcement:', error);
      alert('Failed to save announcement');
    }
  };

  const handleEdit = (announcement) => {
    setEditingId(announcement.id);

    // Helper function to convert UTC to local datetime string for input
    const formatDateForInput = (utcDateString) => {
      if (!utcDateString) return '';
      const date = new Date(utcDateString);
      // Subtract timezone offset to get local time
      const offset = date.getTimezoneOffset() * 60000;
      const localDate = new Date(date.getTime() - offset);
      return localDate.toISOString().slice(0, 16);
    };

    setFormData({
      message: announcement.message,
      targetTier: announcement.targetTier,
      startDate: formatDateForInput(announcement.startDate),
      endDate: formatDateForInput(announcement.endDate),
      linkUrl: announcement.linkUrl || '',
      linkText: announcement.linkText || '',
      isActive: announcement.isActive,
    });
    setShowForm(true);
  };

  const handleToggle = async (id) => {
    try {
      await adminAPI.toggleAnnouncement(id);
      fetchAnnouncements();
    } catch (error) {
      console.error('Error toggling announcement:', error);
      alert('Failed to toggle announcement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) {
      return;
    }

    try {
      await adminAPI.deleteAnnouncement(id);
      alert('Announcement deleted successfully!');
      fetchAnnouncements();
    } catch (error) {
      console.error('Error deleting announcement:', error);
      alert('Failed to delete announcement');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      message: '',
      targetTier: 'all',
      startDate: '',
      endDate: '',
      linkUrl: '',
      linkText: '',
    });
  };

  const getTierBadgeColor = (tier) => {
    switch (tier) {
      case 'all':
        return '#64748b';
      case 'free':
        return '#718096';
      case 'pro':
        return '#3b82f6';
      case 'premium':
        return '#fbbf24';
      case 'paid':
        return '#8b5cf6';
      default:
        return '#64748b';
    }
  };

  if (loading) {
    return (
      <div className="admin-announcements-page">
        <Navbar />
        <div className="admin-announcements-container">
          <div className="loading-state">Loading announcements...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-announcements-page">
      <Navbar />
      <div className="admin-announcements-container">
        <button onClick={() => navigate('/admin')} className="back-button">
          ← Back to Admin Home
        </button>
        <header className="admin-announcements-header">
          <div>
            <h1>Announcements</h1>
            <p>Create and manage notifications for your users</p>
          </div>
          <button
            className="btn btn-success btn-lg"
            onClick={() => setShowForm(true)}
          >
            + Create Announcement
          </button>
        </header>

        {/* Create/Edit Form */}
        {showForm && (
          <div className="announcement-form-card">
            <h2>
              {editingId ? 'Edit Announcement' : 'Create New Announcement'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  placeholder="Enter your announcement message..."
                  required
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="targetTier">Target Audience *</label>
                  <select
                    id="targetTier"
                    value={formData.targetTier}
                    onChange={(e) =>
                      setFormData({ ...formData, targetTier: e.target.value })
                    }
                    required
                  >
                    <option value="all">All Users</option>
                    <option value="free">Free Users Only</option>
                    <option value="pro">Pro Users Only</option>
                    <option value="premium">Premium Users Only</option>
                    <option value="paid">Paid Users (Pro + Premium)</option>
                    <option value="public">Public (Login Page, Not Logged In)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="startDate">Start Date</label>
                  <input
                    type="datetime-local"
                    id="startDate"
                    value={formData.startDate}
                    onChange={(e) =>
                      setFormData({ ...formData, startDate: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="endDate">End Date (Optional)</label>
                  <input
                    type="datetime-local"
                    id="endDate"
                    value={formData.endDate}
                    onChange={(e) =>
                      setFormData({ ...formData, endDate: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="linkText">Button Text (Optional)</label>
                  <input
                    type="text"
                    id="linkText"
                    value={formData.linkText}
                    onChange={(e) =>
                      setFormData({ ...formData, linkText: e.target.value })
                    }
                    placeholder="e.g., Learn More"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="linkUrl">Button Link (Optional)</label>
                  <input
                    type="text"
                    id="linkUrl"
                    value={formData.linkUrl}
                    onChange={(e) =>
                      setFormData({ ...formData, linkUrl: e.target.value })
                    }
                    placeholder="e.g., /membership"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-success">
                  {editingId ? 'Update Announcement' : 'Create Announcement'}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={resetForm}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Announcements List */}
        <div className="announcements-list">
          {announcements.length === 0 ? (
            <div className="empty-state-admin">
              <div className="empty-icon">📢</div>
              <h3>No Announcements Yet</h3>
              <p>
                Create your first announcement to notify users of updates and
                new features
              </p>
            </div>
          ) : (
            announcements.map((announcement) => (
              <div
                key={announcement.id}
                className={`announcement-item ${
                  !announcement.isActive ? 'inactive' : ''
                }`}
              >
                <div className="announcement-item-header">
                  <div className="announcement-status">
                    <span
                      className="tier-badge"
                      style={{
                        backgroundColor: getTierBadgeColor(
                          announcement.targetTier
                        ),
                      }}
                    >
                      {announcement.targetTier}
                    </span>
                    <span
                      className={`status-badge ${
                        announcement.isActive ? 'active' : 'inactive'
                      }`}
                    >
                      {announcement.isActive ? '🟢 Active' : '🔴 Inactive'}
                    </span>
                  </div>
                  <div className="announcement-dates">
                    <small>
                      {new Date(announcement.startDate).toLocaleString()}
                      {announcement.endDate &&
                        ` → ${new Date(announcement.endDate).toLocaleString()}`}
                    </small>
                  </div>
                </div>

                <div className="announcement-message">
                  {announcement.message}
                </div>

                {announcement.linkText && announcement.linkUrl && (
                  <div className="announcement-link-preview">
                    <strong>Button:</strong> "{announcement.linkText}" →{' '}
                    {announcement.linkUrl}
                  </div>
                )}

                <div className="announcement-actions">
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => handleEdit(announcement)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-sm"
                    style={{
                      background: announcement.isActive ? '#fbbf24' : '#48bb78',
                      color: 'white',
                    }}
                    onClick={() => handleToggle(announcement.id)}
                  >
                    {announcement.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDelete(announcement.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminAnnouncements;
